import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import { ParsedUrlQuery } from 'querystring'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  SanityProductCatalogueQuery,
  SanityProductFragment,
  SanityProductPage,
} from '@data/sanity/queries/types/product'
import { getShopifyDomain } from '@lib/shopify/client'
import { getPreview } from '@lib/sanity/client'
import { getAllProductSlugs } from '@lib/sanity/page'
import { getProductCatalogue, getProductPage } from '@lib/sanity/product'
import { useViewContentFacebookEvent } from '@lib/facebook'
import { getAllLocales, Locale } from '@lib/language'
import { useVariantMetadata } from '@lib/metadata'
import { useProductWithInventory } from '@lib/product'
import { ProductVariantSelectionContextProvider } from '@lib/product-variant-selection-context'
import { getReviews, Reviews } from '@lib/review'
import { CustomStaticPropsContext, StaticPathWithSlug } from '@lib/routes'
import { useProductSchema } from '@lib/schema'

import Modules from '@modules/modules'
import Layout from '@components/layout'

interface StaticProductPageParams extends ParsedUrlQuery {
  slug: string
}

interface ProductPageProps {
  locale: Locale
  page: SanityProductPage
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
  reviews?: Reviews
}

interface ProductPageContentProps {
  product: SanityProductFragment
  locale: Locale
  page: SanityProductPage
  site: SanitySiteFragment
  reviews?: Reviews
}

const ProductPageContent = ({
  product,
  locale,
  page,
  site,
  reviews,
}: ProductPageContentProps) => {
  // Get product schema
  const productSchema = useProductSchema(site, product)

  // Trigger Facebook event
  useViewContentFacebookEvent(
    locale,
    product.title,
    site.settings.facebookEvents,
  )

  // Set product variant metadata
  useVariantMetadata()

  if (!page.modules) {
    return null
  }

  return (
    <Layout page={page} site={site} productSchema={productSchema}>
      <Modules modules={page.modules} product={product} reviews={reviews} />
    </Layout>
  )
}

/**
 * The product page.
 */
const ProductPage = ({ locale, page, site, reviews }: ProductPageProps) => {
  const product = useProductWithInventory(page, locale)

  return (
    <ProductVariantSelectionContextProvider product={product}>
      <ProductPageContent
        product={product}
        locale={locale}
        page={page}
        site={site}
        reviews={reviews}
      />
    </ProductVariantSelectionContextProvider>
  )
}

export async function getStaticProps({
  locale,
  params,
  preview,
  previewData,
}: CustomStaticPropsContext<StaticProductPageParams>): Promise<
  GetStaticPropsResult<ProductPageProps>
> {
  const productPage = await getProductPage(
    locale,
    params?.slug ?? '',
    getPreview(preview, previewData),
  )

  if (!productPage.page) {
    return { notFound: true }
  }

  const productCatalogue = await getProductCatalogue(locale)

  // Get Stamped.io reviews
  const shopifyDomain = getShopifyDomain(locale)
  const reviews = await getReviews(
    productPage.page.modules ?? [],
    shopifyDomain,
    productPage.site.settings.stampedApiKey,
    productPage.page.product.id,
  )

  return {
    props: {
      locale,
      page: productPage.page,
      site: productPage.site,
      productCatalogue,
      reviews,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticProductPageParams>
> {
  const allProducts = await getAllProductSlugs()
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlug[] = allProducts
    .map((product) => ({
      ...product,
      locale: product.locale as Locale,
    }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ slug }) => !!slug)
    .map(({ locale, slug }) => ({
      params: {
        slug,
      },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default ProductPage
